import { useCallback, useState } from "react";

import api from "../services/api";
import { i18n } from "../translate/i18n";
import { parseContactConflict } from "../utils/contactConflict";

/**
 * Estado compartilhado do fluxo "definir celular (WhatsApp)" — usado pelo
 * painel de detalhe da Carteira e pelo card do Kanban (SetCelularModal).
 * Único ponto que chama PUT /contatos/celular, para os dois lugares ficarem
 * sempre em sincronia quanto a conflito/erro.
 *
 * Contrato do backend (já implementado e testado, não alterar aqui):
 * - 200 `{ status: "saved", contactId, created, linkedExistingContact }`
 * - 409 `{ status: "conflict", conflictContactId, conflictContactName, linkable }`
 *   — o NOME do outro contato vem nessa resposta (só para exibição, ex:
 *   "abrir cadastro existente"); CPF/telefone dele nunca vêm, e nunca
 *   devemos pedir isso. Quando `linkable` é true, resolve-se reenviando com
 *   `confirmLinkToContactId` = esse id, ou cancelando (não reenviar).
 *   Quando `linkable` é false (dono do número tem CPF/CNPJ diferente), o
 *   backend rejeita a confirmação mesmo assim — a tela só deve oferecer
 *   "informe outro número".
 * - 409 `{ conflict: "contato_existente", reason, contactIds, contactName? }`
 *   (regra R, o que o backend envia hoje): nada foi alterado; a tela só
 *   informa (`contactConflict`), sem reenvio automático.
 * - 403 — não-admin: aviso fixo, sem erro genérico.
 * - 400 — mensagem de erro já genérica vinda do backend; exibida direto,
 *   sem completar com dado local.
 *
 * Nunca loga celular/nome no console em caso de erro — só guarda a
 * mensagem genérica do backend (ou um texto padrão) em `errorMessage`.
 *
 * Identificação do cliente: `cpfCnpj` direto (Carteira, que já tem o
 * documento na tela) OU `policyId` (Kanban — o board não recebe cpfCnpj,
 * dado sensível; o controller resolve o documento no servidor). Use só um
 * dos dois.
 */
export default function useSetCelularWhatsapp({ cpfCnpj, policyId } = {}) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // { conflictContactId, linkable, payload } — payload é o corpo que gerou
  // o 409, guardado para poder reenviar com confirmLinkToContactId sem
  // pedir os dados de novo ao usuário.
  const [conflict, setConflict] = useState(null);

  // Conflito da regra R (contrato novo): { reason, contactIds, contactName? }.
  const [contactConflict, setContactConflict] = useState(null);

  const call = useCallback(async (payload) => {
    setSaving(true);
    setErrorMessage(null);
    setContactConflict(null);
    try {
      const { data } = await api.put("/contatos/celular", payload);
      setSaving(false);
      setConflict(null);
      return data;
    } catch (err) {
      setSaving(false);
      if (err?.response?.status === 403) {
        setErrorMessage(i18n.t("contactConflict.adminOnly"));
        return null;
      }
      const ruleRConflict = err?.response?.status === 409 ? parseContactConflict(err.response?.data) : null;
      if (ruleRConflict) {
        setContactConflict(ruleRConflict);
        return null;
      }
      if (err?.response?.status === 409) {
        const { conflictContactId, conflictContactName, linkable } = err.response?.data || {};
        setConflict({ conflictContactId, conflictContactName, linkable: Boolean(linkable), payload });
        return null;
      }
      const backendMessage = err?.response?.data?.error;
      setErrorMessage(
        typeof backendMessage === "string" && backendMessage
          ? backendMessage
          : "Não foi possível salvar o celular."
      );
      return null;
    }
  }, []);

  const submit = useCallback(
    (celular, name) =>
      call({
        ...(cpfCnpj ? { cpfCnpj } : { policyId }),
        celular,
        ...(name ? { name } : {}),
      }),
    [call, cpfCnpj, policyId]
  );

  const resolveConflict = useCallback(() => {
    if (!conflict) return Promise.resolve(null);
    return call({ ...conflict.payload, confirmLinkToContactId: conflict.conflictContactId });
  }, [call, conflict]);

  const cancelConflict = useCallback(() => {
    setConflict(null);
    setContactConflict(null);
  }, []);

  const reset = useCallback(() => {
    setErrorMessage(null);
    setContactConflict(null);
    setConflict(null);
    setSaving(false);
  }, []);

  return { submit, resolveConflict, cancelConflict, reset, saving, errorMessage, conflict, contactConflict };
}
